import { createRollWatch, type RollData, rollData } from "./feed";

const ROLL = ".odo-dice-roll[id]";
const RETRY_MS = 2000;
// The feed allows 60 loads a minute per address.
const RETRY_MAX_MS = 60_000;

// A <turbo-frame>; Turbo ships no types.
export type FrameElement = HTMLElement & { src: string | null; reload(): void };

// The public table link's way to the campaign's rolls: the token-keyed read that would let it use the feed client was declined,
// so it keeps the old feed's frame, hidden, whose stream appends each roll. `play` hears every roll the frame
// receives live; a load, first or reloaded, is history. A load the server refuses (the feed's rate limit answers an empty 429,
// which Turbo leaves unrendered), one that lost the session and one lost on the network try again, less often each time.
export function watchFrame(frame: FrameElement, { live, play }: { live: () => boolean; play: (data: RollData) => void }) {
	const watch = createRollWatch();
	let primed = false;
	let failures = 0;
	let timer: ReturnType<typeof setTimeout> | undefined;
	const ids = () => [...frame.querySelectorAll(ROLL)].map((element) => element.id);

	const observer = new MutationObserver(() => {
		if (!primed || !live()) return;
		const fresh = watch.fresh(ids());
		if (document.hidden) return;
		for (const id of fresh) {
			const element = document.getElementById(id);
			const data = element && rollData(element);
			if (data) play(data);
		}
	});

	// Turbo repaints before turbo:frame-load: records taken in between are dropped before the rolls count as seen.
	const loaded = () => {
		failures = 0;
		observer.takeRecords();
		watch.prime(ids());
		primed = true;
	};
	const retry = () => {
		primed = false;
		clearTimeout(timer);
		timer = setTimeout(() => frame.reload(), Math.min(RETRY_MS * 2 ** failures, RETRY_MAX_MS));
		failures += 1;
	};
	const rendering = () => {
		primed = false;
	};
	const refused = ({ target, detail }: Event & { detail?: { fetchResponse: { failed: boolean } } }) => {
		if (target === frame && detail?.fetchResponse.failed) retry();
	};
	const errored = ({ target }: Event) => {
		if (target === frame) retry();
	};
	// The login page, with no frame, which Turbo would replace with "Content missing"; a non-ok answer with no frame is a deleted feed.
	const missing = (event: Event & { detail?: { response: { ok: boolean } } }) => {
		event.preventDefault();
		if (event.detail?.response.ok) retry();
	};

	observer.observe(frame, { childList: true, subtree: true });
	const listeners: [string, (event: never) => void][] = [
		["turbo:before-frame-render", rendering],
		["turbo:frame-load", loaded],
		["turbo:before-fetch-response", refused],
		["turbo:frame-missing", missing],
		["turbo:fetch-request-error", errored],
	];
	for (const [name, listener] of listeners) frame.addEventListener(name, listener);
	// The attribute: Turbo's `complete` property is true before a load starts.
	if (frame.hasAttribute("complete")) loaded();

	return {
		// Rolls made while the board was offline may never have reached the frame's stream.
		reload() {
			if (!frame.src) return;
			primed = false;
			frame.reload();
		},
		stop() {
			clearTimeout(timer);
			observer.disconnect();
			for (const [name, listener] of listeners) frame.removeEventListener(name, listener);
		},
	};
}
